import React, { useContext, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import {
  FormControl,
  FormLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Mail, Lock } from 'lucide-react';

import { LoadingButton } from '../../Components/Loaders';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

import { object, string } from 'yup';

import { AdminAxios } from '../../Assets/Axios/Axios';
import AuthContext from '../../Context/AuthContext';
import ToastContext from '../../Context/ToastContext';

import { COLORS } from '../../Constant/COLORS';
import { USER_TYPES } from '../../Constant/Enums';

import PasswordField from '../../Components/PasswordField';

import { getClearedErrorObj } from '../../Helpers/index';
import FirebaseAdmin from '../../lib/firebaseAdmin';
import { signOutFirebaseUser } from '../../lib/firebaseClients';
import { localStorageData, LOGOUT } from '../../lib/Token';
import { CurrentAdminQuery } from '../../Server/Query';

const Login = () => {
  const { setUser } = useContext(AuthContext);
  const { showToastMessage } = useContext(ToastContext);

  const [inputs, setInputs] = useState({ email: '', password: '' });
  const [error, setError] = useState({ email: null, password: null });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  function handleChange(event) {
    const { name, value } = event.target;
    setInputs(currentState => ({ ...currentState, [name]: value }));
  }

  const handleSubmit = async event => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(prevErrors => getClearedErrorObj(prevErrors));
    try {
      const validatedData = await schema.validate(inputs, {
        abortEarly: false,
      });

      const signInResult = await FirebaseAdmin.signIn(
        validatedData.email,
        validatedData.password
      );

      if (signInResult.errors) {
        switch (signInResult.errors) {
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
          case 'auth/user-not-found':
            setError(currentErrors => ({
              ...currentErrors,
              email: 'Email or Password is incorrect',
            }));
            return undefined;
          case 'auth/user-disabled':
            showToastMessage('Your admin account has been disabled', 'info');
            return undefined;
          default:
            showToastMessage('Something went wrong while trying to login.');
            return undefined;
        }
      }

      localStorageData({ type: USER_TYPES.ADMIN });

      const response = await AdminAxios(CurrentAdminQuery);

      if (response?.errors || !response?.data?.currentAdmin) {
        await signOutFirebaseUser(USER_TYPES.ADMIN).catch(() => undefined);
        LOGOUT();
        setError(currentErrors => ({
          ...currentErrors,
          email: 'Email or Password is incorrect',
        }));
        return undefined;
      }

      if (!response.data.currentAdmin.isActive) {
        await signOutFirebaseUser(USER_TYPES.ADMIN).catch(() => undefined);
        LOGOUT();
        showToastMessage('Your admin account has been disabled', 'info');
        return undefined;
      }

      const user = {
        type: USER_TYPES.ADMIN,
        userID: response.data.currentAdmin.id,
      };

      localStorageData(user);
      setUser(user);
      navigate(`/admin/dashboard`);
    } catch (error) {
      if (error.name === 'ValidationError') {
        const [validationError] = error.inner;
        setError(prevErrors => ({
          ...prevErrors,
          [validationError.path]: validationError.message,
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack
      gap={3}
      px={{ xs: 3, md: 15 }}
      mx='auto'
      maxWidth='400px'
      component='form'
      onSubmit={handleSubmit}
      alignItems='center'
      minHeight='100vh'
      justifyContent='center'
    >
      <AdminPanelSettingsIcon
        sx={{
          backgroundColor: COLORS.teal,
          borderRadius: '100%',
          color: 'white',
          fontSize: 63,
          padding: 1,
        }}
      />

      <Stack alignItems='center' gap={0.5}>
        <Typography variant='h4'>Admin Portal</Typography>
        <Typography color={COLORS.inkSoft}>
          Sign in to manage Evento
        </Typography>
      </Stack>

      <FormControl fullWidth>
        <FormLabel>
          <Typography color='black' fontWeight={500} mb={1}>
            Email
          </Typography>
        </FormLabel>
        <TextField
          placeholder='john@gmail.com'
          value={inputs.email}
          name='email'
          onChange={handleChange}
          error={Boolean(error.email)}
          helperText={error.email}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Mail size={18} color={COLORS.inkSoft} strokeWidth={1.8} />
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      <FormControl fullWidth>
        <FormLabel>
          <Typography color='black' fontWeight={500} mb={1}>
            Password
          </Typography>
        </FormLabel>

        <PasswordField
          autoComplete='current-password'
          error={Boolean(error.password)}
          helperText={error.password}
          value={inputs.password}
          name='password'
          onChange={handleChange}
          placeholder='************'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Lock size={18} color={COLORS.inkSoft} strokeWidth={1.8} />
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      <LoadingButton
        type='submit'
        variant='outlined'
        color='blue'
        isLoading={isSubmitting}
        sx={{ color: COLORS.blue, fontWeight: 500, px: 5, mt: 1 }}
      >
        Login
      </LoadingButton>
    </Stack>
  );
};

const schema = object({
  email: string().trim().required().email().label('Email'),
  password: string().required().label('Password'),
});

export default Login;
